/**
 * The experiment catalog — the code-side half of an A/B test.
 *
 * Split of responsibility, and it matters:
 *   catalog (here) → which experiments exist, and which arms each declares
 *   database (Solo) → weights, status, enrolFrom, rolloutArm
 *   database         → who got which arm
 *
 * Anything code branches on lives here so it is typed and reviewable; anything
 * ops tunes without a deploy stays in the row. The CMS rejects an edit that
 * renames an arm of a catalogued experiment, so the two cannot drift — a renamed
 * arm would otherwise fall through every `switch` and silently become control.
 */

/** Arena-only: the Mixpanel listener writes exposures into the Arena project. */
type ExperimentProduct = "arena";

/**
 * `<product>_<surface>_v<n>`. The version suffix is not decoration: re-running a
 * test after changing what an arm means needs a new key, or the old cohort's
 * assignments silently join the new test.
 */
export type ExperimentName = `${ExperimentProduct}_${string}_v${number}`;

/** Every experiment the code knows about. Add here first, then seed the row. */
export const experimentCatalog = {
  arena_onboarding_v1: ["skippable", "unskippable", "skippable_after_first"],
  // `mandatory` is also what an UNENROLLED (null arm) user gets — see ArmOf's
  // contract: null is not control, every call site branches explicitly.
  arena_qualification_v1: ["mandatory", "skippable"],
} as const satisfies Record<ExperimentName, readonly [string, ...string[]]>;

export type ExperimentCatalog = typeof experimentCatalog;
export type ExperimentKey = keyof ExperimentCatalog;

/** The arms a given experiment declares, as a union — `"skippable" | "unskippable" | …`. */
export type ArmOf<K extends ExperimentKey> = ExperimentCatalog[K][number];

/** Named access, so read sites don't retype the string. Mirrors `FEATURE_FLAGS`. */
export const EXPERIMENTS = {
  ARENA_ONBOARDING_V1: "arena_onboarding_v1",
  ARENA_QUALIFICATION_V1: "arena_qualification_v1",
} as const satisfies Record<string, ExperimentKey>;

export function isCatalogedExperiment(key: string): key is ExperimentKey {
  return Object.hasOwn(experimentCatalog, key);
}

/**
 * The arms the code expects for a key, or null when the experiment isn't
 * catalogued. Uncatalogued experiments are allowed — a pure analytics holdout
 * that nothing branches on needs no entry — they simply get no type safety and
 * no CMS enforcement.
 */
export function catalogedArmsFor(key: string): readonly string[] | null {
  return isCatalogedExperiment(key) ? experimentCatalog[key] : null;
}
